import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { usage } from '../../api/devices.ts';
import { listSharedFolders } from '../../api/share.ts';
import { formatBytes } from '../../lib/format.ts';
import { IconTrash } from '../../lib/icons.tsx';

const KIND_LABEL: Record<string, string> = {
  image: '이미지', video: '동영상', audio: '오디오', document: '문서', other: '기타',
};
const KIND_COLOR: Record<string, string> = {
  image: 'var(--c-accent)', video: '#8b5cf6', audio: '#14b8a6',
  document: '#f59e0b', other: 'var(--c-text-faint)',
};

export function StorageTab() {
  const { data, isLoading } = useQuery({ queryKey: ['usage'], queryFn: usage });
  const shared = useQuery({ queryKey: ['shared-folders'], queryFn: listSharedFolders });

  if (isLoading || !data) return <span className="spinner" />;

  const percent = Math.min(100, (data.used_bytes / Math.max(1, data.quota_bytes)) * 100);
  const level = percent >= 95 ? 'full' : percent >= 80 ? 'warn' : 'ok';
  const total = data.by_type.reduce((sum, r) => sum + r.bytes, 0);

  return (
    <>
      <section className="panel">
        <h2>저장 공간</h2>
        <p className="panel__hint">
          휴지통에 있는 파일과 이전 버전도 사용량에 포함됩니다.
        </p>

        <div className="quota__bar" style={{ height: 10 }}>
          <div className="quota__fill" data-level={level} style={{ width: `${percent}%` }} />
        </div>
        <p className="quota__text" style={{ fontSize: 14 }}>
          <strong>{formatBytes(data.used_bytes)}</strong> / {formatBytes(data.quota_bytes)} 사용
          {' '}({percent.toFixed(1)}%) · 남은 용량 {formatBytes(data.available_bytes)}
        </p>

        {level !== 'ok' && (
          <div className="banner" style={{ marginTop: 12, borderRadius: 'var(--r-md)' }}>
            저장 공간이 거의 찼습니다. 휴지통을 비우면 {formatBytes(data.trash_bytes)} 를 확보할 수 있습니다.
          </div>
        )}
      </section>

      <section className="panel">
        <h2>종류별 사용량</h2>
        {total > 0 && (
          <div
            className="row"
            style={{
              gap: 2, height: 10, borderRadius: 'var(--r-full)',
              overflow: 'hidden', marginBottom: 16,
            }}
          >
            {data.by_type.map((row) => (
              <div
                key={row.kind}
                title={`${KIND_LABEL[row.kind] ?? row.kind} ${formatBytes(row.bytes)}`}
                style={{
                  width: `${(row.bytes / total) * 100}%`,
                  height: '100%',
                  background: KIND_COLOR[row.kind] ?? 'var(--c-border-strong)',
                }}
              />
            ))}
          </div>
        )}
        {data.by_type.length === 0 && <p className="muted">아직 파일이 없습니다.</p>}
        {data.by_type.map((row) => (
          <div key={row.kind} className="panel__row">
            <span
              style={{
                width: 10, height: 10, borderRadius: 3,
                background: KIND_COLOR[row.kind] ?? 'var(--c-border-strong)',
              }}
            />
            <span className="spacer">{KIND_LABEL[row.kind] ?? row.kind}</span>
            <span className="muted">{row.count.toLocaleString()}개</span>
            <strong style={{ minWidth: 80, textAlign: 'right' }}>{formatBytes(row.bytes)}</strong>
          </div>
        ))}
      </section>

      <section className="panel">
        <h2>정리하기</h2>
        <div className="panel__row">
          <IconTrash size={18} />
          <div className="spacer">
            <div style={{ fontSize: 13 }}>휴지통</div>
            <div className="faint" style={{ fontSize: 12 }}>
              {formatBytes(data.trash_bytes)} 차지하는 중
            </div>
          </div>
          <Link className="btn btn--sm" to="/trash">휴지통 열기</Link>
        </div>
        {(shared.data?.items ?? []).filter((f) => f.is_owner).map((folder) => (
          <div key={folder.namespace_id} className="panel__row">
            <div className="spacer">
              <div style={{ fontSize: 13 }}>공유 폴더 · {folder.name}</div>
              <div className="faint" style={{ fontSize: 12 }}>
                {folder.file_count}개 파일 · 참여자가 올린 파일도 내 용량에 포함됩니다
              </div>
            </div>
            <strong>{formatBytes(folder.total_size)}</strong>
          </div>
        ))}
      </section>
    </>
  );
}
